// E3 (owner, 2026-08-13) — **NO EMAIL LEAVES THIS HOUSE WITHOUT THE AESTHETIC, EVER, NO EXCEPTIONS.**
//
// HER ORDER, after her daughter read the live funnel mail: every outbound member-facing email —
// funnel readings, the welcome, grace reminders, broadcasts, everything now and forever — renders
// inside ONE SHARED HOUSE EMAIL SHELL: **her header image at the top, the dark gold-bordered body,
// house typography, and a CALL TO ACTION appropriate to the mail's purpose.**
//
// **MEASURED FIRST (R25/R180), AND HER COMPLAINT IS EXACT.** Three composers send from this house
// and every one of them hand-rolled its own markup: `funnelEmailBody` (a bare `<div>` with an
// `<h1>12th House</h1>`), `broadcastEmailHtml` (a `<div>` and an `<h1>`), and `graceEmailBody` —
// the nakedest of the three — **three unstyled `<p>` tags and a bare `<a>`.** Not one of them
// carried her header image. Three copies of a look is three answers to what this house looks like
// (R28/R29), and the copy that drifts is the one nobody is reading.
//
// **THE SHELL IS ON THE SERVER, NOT IN `src/lib`.** `broadcastEmailHtml` lives in `src/broadcasts.js`
// because the owner's dashboard previews a broadcast before she sends it, so the shell has to be
// reachable from BOTH — and `src/lib/*.ts` is CLIENT-ONLY by standing law. This module imports only
// `src/siteMeta.js`, so either side can read it.
//
// **EVERY COLOUR IS DECLARED ONCE HERE AND NOWHERE ELSE.** An email cannot read a CSS custom
// property — there is no `:root` in a mail client — so the house tokens arrive as literals, and the
// whole point of this module is that they are literals in ONE place. THE COLOUR LAW binds: these are
// her approved black/cream/gold, not invented near-misses.
import { SITE_URL, SITE_NAME } from "../src/siteMeta.js";

// **SAMPLED FROM HER OWN MASTER, NOT CHOSEN.** Read with `sharp` off `example welcome email.png.png`
// rather than eyeballed, so every number below is hers rather than a session's approximation:
// `node scripts/item4-sample-master.mjs` re-runs the measurement and prints the grid.
//
// **ROUND 3's SAMPLED GROUNDS (`#121312` page, `#111111` panel) ARE SUPERSEDED BY ROUND 4's PLUM
// BELOW** and are recorded here rather than left standing as though they were still what ships
// (R68: a receipt that no longer applies reads as the current law). Round 3's reason for them was
// real and is why the ORNAMENTS then had to match a ground exactly — **round 4 removed that
// requirement at its root by cutting them with a real alpha channel**, so no panel colour can seam
// against her art again, whatever the panel becomes.
//
// The welcome master's button plate, SAMPLED from it (`#301425`) rather than invented. The first
// build guessed `#3b1030`, which is a different purple — THE COLOUR LAW's own point, one surface on.
export const EMAIL_PLUM = "#301425";

// >>> **ROUND 4 (2026-08-13): THE PANEL IS A PLUM, AND IT IS DERIVED FROM HER OWN PLATE RATHER THAN
// PICKED.** <<< Her gap 4: *"the body background still is not the plum from her example mockup...
// apply the true plum tone, not near-black."*
//
// **AND THE MEASUREMENT DISAGREES WITH HER INSTRUCTION, WHICH IS REPORTED RATHER THAN QUIETLY
// RESOLVED EITHER WAY (R19/R17/R160).** Sampled across her master at nine heights down both gutters,
// the panel interior really is `#111212`..`#121314` — a NEUTRAL near-black, with no plum in it. The
// only plum in that file is the button plate and the velvet in the banner. So *match the mockup* and
// *make the panel plum* are, here, two different orders.
//
// **HER LATEST WORD WINS, AND THE TONE IS STILL HERS RATHER THAN INVENTED.** The panel is her own
// approved `EMAIL_PLUM` carried down to the darkness her master's panel really has — the HUE is
// hers, the WEIGHT is her mockup's. A hand-picked purple would be a third answer to what this
// house's plum is (THE COLOUR LAW), and the derivation is what makes it impossible: re-tune
// `EMAIL_PLUM` and the panel follows it.
const hex = (r, g, b) => `#${[r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")).join("")}`;
const towardBlack = (color, keep) => {
  const n = parseInt(String(color).slice(1), 16);
  return hex(((n >> 16) & 255) * keep, ((n >> 8) & 255) * keep, (n & 255) * keep);
};
/** How much of her plate survives in each ground. Named, so a re-tune is a deliberate act. */
export const EMAIL_PANEL_KEEP = 0.62;
export const EMAIL_GROUND_KEEP = 0.34;

export const EMAIL_INK = {
  ground: towardBlack(EMAIL_PLUM, EMAIL_GROUND_KEEP),  // the page behind the frame
  panel: towardBlack(EMAIL_PLUM, EMAIL_PANEL_KEEP),    // her body, in her plum
  body: "#f4f1ea",     // the house cream
  gold: "#d8a94b",     // the house gold
  rule: "rgba(216,169,75,0.34)",
  soft: "rgba(244,241,234,0.82)",
};

// **HER HEADER IMAGE, AND IT IS HER OWN FILE AT HER OWN DOMAIN.** An email cannot carry a relative
// path — the recipient's client fetches it from the open internet — so it is absolute, derived from
// `SITE_URL` rather than typed, which is what keeps it correct if the domain ever moves.
//
// >>> **HER WARDROBE TEST (2026-08-13) MOVED THIS TO HER BANNER, AND IT IS THE ONE CORRECTION THAT
// DRESSES ALL THREE CLASSES.** <<< Her order for the broadcast and the funnel samples is *"rendered
// through the shell WITH THE HEADER"* and *"the header, the dress"* — and the header this layout drew
// was `12house-logo.png`, a **1916x821, 1.4MB** logo lockup centred at 260px. So her welcome wore her
// banner while every other mail in the house wore a small different picture: one shell, two headers,
// which is the drift E3 exists to end (R28/R29).
//
// It is her own `email header.png`, cut by `build-email-assets.mjs`, **full width and edge to edge**
// exactly as the framed layout draws it — and it is **169KB against 1.4MB**, so the heavy asset E3
// flagged for her word is gone as a side effect rather than as a separate round.
export const EMAIL_HEADER_IMAGE = `${SITE_URL}/email-header.jpg`;
export const EMAIL_HEADER_WIDTH = 680;

const esc = (s) => String(s == null ? "" : s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

// ============================================================================================
// **THE HOUSE'S THREE DRAWN THINGS, LIFTED SO THERE IS ONE OF EACH (2026-08-14, the BLOCK
// COMPOSER).**
//
// Her block composer stacks a HEADING, a BUTTON, an IMAGE and a DIVIDER into a body. Every one of
// those already existed in this file — as a `<h1>` inside `houseEmailHtml`, as two button faces
// inside `houseEmailHtml`, and as `closeMark` inside `houseFramedEmailHtml` — and every one of them
// was written INLINE where only one caller could reach it. **A second spelling of the house's
// divider is a second answer to what the house's divider is** (R28/R29), and the copy that drifts is
// the marketing one she looks at every week.
//
// So they are named functions here, read by the layouts that already drew them AND by the block
// renderer below. **Nothing outside this file types a colour** — E3's law, and the reason the block
// MODEL lives in `src/broadcasts.js` while the block MARKUP lives here.
// ============================================================================================

/** Her ornament. One character, one declaration — the framed close and a divider block share it. */
export const EMAIL_ORNAMENT = "&#10022;";

/**
 * The house divider: a gold rule broken by her mark. A `<table>` rather than an `<hr>` because
 * Outlook's Word engine draws an `<hr>` at its own width in its own colour and ignores both.
 */
export function houseEmailDivider({ top = 30, bottom = 0 } = {}) {
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:${top}px 0 ${bottom}px">`
    + `<tr><td style="border-top:1px solid ${EMAIL_INK.rule};font-size:0;line-height:0">&nbsp;</td>`
    + `<td width="26" style="text-align:center;color:${EMAIL_INK.gold};font-size:13px">${EMAIL_ORNAMENT}</td>`
    + `<td style="border-top:1px solid ${EMAIL_INK.rule};font-size:0;line-height:0">&nbsp;</td></tr></table>`;
}

/**
 * **THE BUTTON, AND BOTH ITS FACES.** `plum` is her welcome master's plate; the default is the gold
 * pill every other mail wears (FREE-1's `action` default: an omitted argument is today's behaviour).
 * A real `<a>` with real padding, never a background image — an image button is a grey box in every
 * client that blocks images by default.
 */
export function houseEmailButton({ label = "Open", href = "", variant = "gold", top = 26 } = {}) {
  if (!href) return "";
  const plum = variant === "plum";
  return `<p style="margin:${top}px 0 0"><a href="${esc(href)}" style="${plum
    ? `color:${EMAIL_INK.gold};background:${EMAIL_PLUM};border:1px solid ${EMAIL_INK.gold};border-radius:6px;letter-spacing:2px;text-transform:uppercase`
    : `color:#14110b;background:${EMAIL_INK.gold};border:1px solid ${EMAIL_INK.gold};border-radius:999px`
  };display:inline-block;padding:14px 26px;font-weight:bold;text-decoration:none;font-size:16px">${esc(label || "Open")}</a></p>`;
}

/**
 * **ONE BLOCK, DRAWN.** The block MODEL — which kinds exist, what each one must carry, and which
 * URLs are legal in a mail — is `src/broadcasts.js`'s and is validated there. This function is the
 * MARKUP, and it is here because E3's law is that the house look exists in exactly one file.
 *
 * **A STRING IS A PARAGRAPH, WHICH IS WHAT EVERY EXISTING CALLER HANDS OVER**, so the funnel mail,
 * the welcome and the grace reminders render byte-identically to the day before this existed.
 *
 * **A KIND THIS FUNCTION CANNOT DRAW DRAWS NOTHING, RATHER THAN DRAWING MACHINERY.** The law refuses
 * an unrostered kind upstream, so nothing can reach here that was not checked — and if something
 * ever does, an empty space in a preview she is reading is a smaller loss than `[object Object]` in
 * a thousand inboxes (R19).
 */
export function houseEmailBlockHtml(block) {
  if (block == null) return "";
  if (typeof block === "string") {
    const t = block.trim();
    return t ? `<p style="margin:0 0 18px;font-size:17px;line-height:28px;color:${EMAIL_INK.body};white-space:pre-wrap">${esc(t).replace(/\n/g, "<br />")}</p>` : "";
  }
  const kind = String(block.kind || "");
  if (kind === "text") return houseEmailBlockHtml(String(block.text || ""));
  if (kind === "heading") {
    const t = String(block.text || "").trim();
    return t ? `<p style="margin:26px 0 14px;font-size:20px;line-height:28px;color:${EMAIL_INK.gold}">${esc(t)}</p>` : "";
  }
  if (kind === "button") return houseEmailButton({ label: block.label, href: block.href, top: 22 });
  if (kind === "image") {
    const src = String(block.src || "");
    // The `width` ATTRIBUTE as well as the style, because Outlook ignores CSS width on images and
    // would otherwise paint her picture at its full resolution across the mail.
    return src
      ? `<p style="margin:0 0 18px"><img src="${esc(src)}" width="${EMAIL_HEADER_WIDTH}" alt="${esc(block.alt || "")}" style="width:100%;max-width:${EMAIL_HEADER_WIDTH}px;height:auto;border:0;display:block" /></p>`
      : "";
  }
  if (kind === "divider") return houseEmailDivider({ top: 24, bottom: 24 });
  return "";
}

/**
 * The text half of one block. **A block with no words contributes nothing**, which is why the block
 * law refuses a mail made only of images and dividers rather than letting one reach the send and
 * fail there with no sentence (a mail with no text part is also the shape a spam filter reads worst).
 */
export function houseEmailBlockText(block) {
  if (block == null) return "";
  if (typeof block === "string") return block.trim();
  const kind = String(block.kind || "");
  if (kind === "text" || kind === "heading") return String(block.text || "").trim();
  if (kind === "button") {
    const href = String(block.href || "").trim();
    return href ? `${String(block.label || "Open").trim()}: ${href}` : "";
  }
  // A decorative image has an empty alt and contributes nothing; a described one contributes its
  // description, which is what alt text IS.
  if (kind === "image") return String(block.alt || "").trim();
  return "";
}

/**
 * **THE SHELL. Every outbound email in this house renders through it.**
 *
 * `blocks` are plain paragraphs OR the typed blocks above, and they are ESCAPED here, so no caller
 * can hand-build markup and no caller has to remember to escape — which is the second thing three
 * separate composers get to be inconsistent about.
 *
 * **THE CTA IS OPTIONAL AND ITS ABSENCE IS A DECISION, NOT A DEFAULT.** Her clause says *a call to
 * action appropriate to the mail's purpose*; a mail with genuinely nothing to tap (a receipt, a
 * notice) must not grow an invented button, and `null` says so out loud at the call site.
 */
// **TWO OPTIONAL SLOTS, ADDED FOR ITEM 4's MASTER AND DEFAULTED TO NOTHING.** Her welcome master has
// a hero above the heading and a centred gold rule partway down the body — structure the funnel and
// the reminders do not have. They are SLOTS rather than a second shell, because a second shell is
// exactly what E3 exists to prevent; and they default to empty, so every existing caller renders
// byte-identically (FREE-1's `action` default: forgetting the argument is today's behaviour).
export function houseEmailHtml({
  heading = "", blocks = [], cta = null, footerHtml = "", preheader = "",
  aboveHeading = "", beforeIndex = null, hero = null, panelFooterHtml = "",
} = {}) {
  // A block that draws nothing is dropped rather than leaving an empty slot, which is what the
  // string path has always done with a blank paragraph — so `beforeIndex` counts what is really
  // drawn rather than what was handed in.
  const drawn = (Array.isArray(blocks) ? blocks : [blocks])
    .map((b) => houseEmailBlockHtml(b))
    .filter(Boolean);

  const at = beforeIndex && Number.isInteger(beforeIndex.index) ? beforeIndex.index : -1;
  const body = drawn
    .map((html, i) => `${i === at ? String(beforeIndex.html || "") : ""}${html}`)
    .join("");

  // The preheader is the grey line a mail client shows beside the subject in the list. Left out, the
  // client makes one up out of the first thing it finds — which on an image-led mail is the ALT text
  // of her logo, so every mail in her members' inboxes would preview as the site name.
  const pre = preheader
    ? `<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent">${esc(preheader)}</div>`
    : "";

  // **TWO BUTTON FACES, AND THE SECOND ONE IS HER VISUAL MASTER'S RATHER THAN A PREFERENCE.** The
  // welcome email's master (`example welcome email.png.png`) draws a PLUM plate with a gold rule and
  // gold lettering; the funnel's is the gold pill. R-H's ruling is that the master outranks the copy
  // block where they differ, so the variant is carried here rather than the mail being quietly given
  // the wrong button. **THE DEFAULT IS THE GOLD PILL**, so every existing caller is untouched
  // (FREE-1's `action` default: forgetting the argument is today's behaviour, never something worse).
  // Both are `<a>` with real padding rather than a background image, which is what "bulletproof"
  // means here — an image button is a grey box in every client that blocks images by default.
  // **LIFTED TO `houseEmailButton` (2026-08-14).** Her block composer draws a button too, and two
  // spellings of the house's button is exactly the drift E3 exists to end (R28/R29).
  const button = cta ? houseEmailButton({ label: cta.label, href: cta.href, variant: cta.variant }) : "";

  return `<!doctype html><html><body style="margin:0;padding:0;background:${EMAIL_INK.ground}">`
    + pre
    // **THE COLUMN IS THE SAME WIDTH AS THE FRAMED LAYOUT'S** (her wardrobe order: the three classes
    // are one family), and the padding sits INSIDE the panel rather than around the header, so her
    // banner runs edge to edge here exactly as it does there.
    + `<div style="max-width:${FRAMED_LAYOUT.width}px;margin:0 auto;padding:0 0 28px;background:${EMAIL_INK.ground};font-family:Georgia,'Times New Roman',serif">`
    // The header image is a real `<img>` with a `width` ATTRIBUTE as well as a style, because Outlook
    // ignores CSS width on images and would otherwise paint it at its full resolution across.
    // **A `hero` REPLACES THE HEADER RATHER THAN SITTING ABOVE IT, AND THAT IS HER RULING.** Her
    // banner ALREADY CARRIES the logo, WELCOME HOME and the plate — so drawing a lockup above it
    // would print her wordmark twice.
    + (hero && hero.src
      ? `<p style="margin:0 0 20px"><a href="${esc(hero.href || SITE_URL)}"><img src="${esc(hero.src)}" width="${EMAIL_HEADER_WIDTH}" alt="${esc(hero.alt || SITE_NAME)}" style="width:100%;max-width:${EMAIL_HEADER_WIDTH}px;height:auto;border:0;display:block" /></a></p>`
      : `<p style="margin:0 0 22px"><a href="${esc(SITE_URL)}"><img src="${esc(EMAIL_HEADER_IMAGE)}" width="${EMAIL_HEADER_WIDTH}" alt="${esc(SITE_NAME)}" style="width:100%;max-width:${EMAIL_HEADER_WIDTH}px;height:auto;border:0;display:block" /></a></p>`)
    // The gold-bordered body. Her words sit INSIDE the rule, which is the whole look.
    + `<div style="border:1px solid ${EMAIL_INK.rule};border-radius:14px;padding:26px 22px;background:${EMAIL_INK.panel}">`
    + String(aboveHeading || "")
    + (heading ? `<h1 style="margin:0 0 18px;font-size:22px;line-height:30px;color:${EMAIL_INK.gold};font-weight:normal">${esc(heading)}</h1>` : "")
    + body
    + button
    // ITEM 4: her master closes INSIDE the gold border - the ornament, the quiet lines and the
    // sign-off are all within the panel, not below it. A slot rather than a second shell.
    + String(panelFooterHtml || "")
    + `</div>`
    + (footerHtml || "")
    + `</div></body></html>`;
}

/**
 * **THE PLAIN-TEXT HALF, AND IT IS NOT AN AFTERTHOUGHT.** Her report on the live funnel mail was
 * that its text part was **truncated mid-word** (*"Want to see wha"*). Nothing here truncates: the
 * paragraphs are joined whole, and the CTA's URL is written out in full because a text-part reader
 * has nothing to tap.
 */
export function houseEmailText({ heading = "", blocks = [], cta = null, footerText = "" } = {}) {
  const paragraphs = (Array.isArray(blocks) ? blocks : [blocks])
    .map((b) => houseEmailBlockText(b))
    .filter(Boolean);
  const parts = [];
  if (heading) parts.push(String(heading).trim());
  parts.push(...paragraphs);
  if (cta && cta.href) parts.push(`${String(cta.label || "Open").trim()}: ${String(cta.href).trim()}`);
  const tail = String(footerText || "");
  return `${parts.join("\n\n")}${tail}`;
}

// ============================================================================================
// **THE SECOND HOUSE LAYOUT — THE FRAMED ONE, HER VISUAL MASTER'S (ITEM 4, moved here 2026-08-13).**
//
// >>> **IT IS A SECOND LAYOUT IN THE ONE SHELL MODULE, AND NEVER A SECOND SHELL.** <<< E3's whole
// clause is that the house look lives in ONE file, and it was already breached the day this layout
// was written: `server/welcome-email.js` composed its own `<table>`s, its own button and its own
// panel, and **E3's gate went GREEN over it** — because that gate asked whether the sender IMPORTS
// the shell, and whether the binding it sends is named `…EmailBody`. Both were true of a fourth
// hand-rolled composer. **A guard answering an easier question than the thing it guards** (this
// house's most-paid shape), on the round written to end exactly that.
//
// **WHY IT IS A LAYOUT RATHER THAN MORE SLOTS ON `houseEmailHtml`.** That was tried and her eye
// rejected it (ITEM 4, round 2): the flow layout centres a padded column on the ground, and her
// master is a full-bleed banner with her own sliced gold frame butting straight onto it and her
// ornaments layered behind the words. Expressed as slots it drew her banner as a floating thumbnail
// with a band of raw background under it. The two layouts are genuinely different SHAPES; what they
// must never be is two answers to what the house's colours, escaping and text part are — and those
// stay one declaration here.
//
// **EVERY COLOUR IS `EMAIL_INK`/`EMAIL_PLUM` ABOVE. NOTHING BELOW TYPES A HEX.**
// ============================================================================================

/**
 * Her measured rhythm. Named so a change is deliberate rather than drifted.
 *
 * >>> **ROUND 4 (2026-08-13) — HER GAP 1, AND THE DIAGNOSIS IS A MEASUREMENT RATHER THAN A GUESS.**
 * <<< Her report: *"on desktop mail the email renders at mobile width, the header shrunken, the
 * column narrow, black wasteland on both sides."*
 *
 * **IT WAS NOT AN INVERTED MEDIA QUERY AND NOT A MISSING WIDTH — THERE WERE NO MEDIA QUERIES AT ALL,
 * AND THE OUTER TABLE ALREADY CARRIED `width="100%"` WITH THE INNER ONE AT `width="600"`.** So the
 * mail really was rendering at its declared width; **600px was simply too narrow, and it is narrow
 * for a reason that is in her own file.** Her master is **941px wide with a ~905px column**
 * (measured: the frame runs x=18..923). A layout built at 600 reproduces her design at **66% scale**
 * — every word, her banner and her flourishes all a third smaller than she drew them — which is
 * exactly *"shrunken and terrible"*, and on a wide desktop the ground either side of it is the
 * *"black wasteland"*.
 *
 * **680, AND THE NUMBER IS A JUDGEMENT STATED IN THE OPEN.** Her floor is *"600-640px minimum, wider
 * if the mockup proportions want it"*, and her proportions want 905 — which no mail client is safe
 * at (Outlook's reading pane and Gmail's web pane both cut well below it, and a mail wider than the
 * pane scrolls sideways, which is worse than narrow). 680 is comfortably past her floor, renders
 * whole in every major client, and lifts her design from 66% to **75%** of the size she drew.
 */
export const FRAMED_LAYOUT = { width: 680, pad: 46, padPhone: 22, line: "1.75", gap: 22, size: 17 };

/**
 * **THE ONE `<style>` BLOCK, AND WHAT IT IS AND IS NOT ALLOWED TO CARRY.**
 *
 * Media queries in a `<head>` style block are honoured by the Gmail apps, Apple Mail and iOS Mail,
 * and STRIPPED by some others. So the rule here is that **the inline styles are the truth and this
 * block is only ever an improvement on a phone** — nothing a member needs is only in here. A client
 * that drops it shows the desktop banner at full width and the desktop padding, which is a correct
 * mail rather than a broken one (R19: the failure mode of the thing that can be stripped is today's
 * behaviour and never something worse).
 */
export function framedEmailStyle({ width = FRAMED_LAYOUT.width, pad = FRAMED_LAYOUT.pad, padPhone = FRAMED_LAYOUT.padPhone } = {}) {
  return `<style>
@media only screen and (max-width:${width - 1}px){
  .hx-pad{padding-left:${padPhone}px !important;padding-right:${padPhone}px !important}
  .hx-cta{padding-left:16px !important;padding-right:16px !important;font-size:14px !important;letter-spacing:1px !important}
  .hx-wide{display:none !important;max-height:0 !important;overflow:hidden !important}
  .hx-narrow{display:block !important;max-height:none !important;overflow:visible !important}
}
</style>`;
}

/**
 * **THE FRAMED LAYOUT.** A single table at her own width: her full-bleed banner, then ONE plum panel
 * outlined in gold carrying every word. Nothing floats, nothing is stamped on the panel, and nothing
 * is centred inside a wider black field.
 *
 * Every string is ESCAPED here, exactly as the flow layout escapes, so no caller can hand-build
 * markup and no caller has to remember — which is the second thing separate composers get to be
 * inconsistent about.
 *
 * Every slot DEFAULTS TO NOTHING, so a mail with no banner and no button still renders a legible
 * framed column rather than throwing (FREE-1's `action` default: omitting an argument is never worse
 * than today's behaviour).
 */
export function houseFramedEmailHtml({
  preheader = "", href = SITE_URL,
  banner = null,      // { src, narrow, alt } — full-bleed, linked home; `narrow` is the phone cut
  heading = "", blocks = [], rule = "", afterRule = [],
  cta = null,         // { label, href } — her master's plum plate
  footerText = "", signoff = "", siteLine = "",
} = {}) {
  const W = FRAMED_LAYOUT.width;
  const home = String(href || SITE_URL);
  const para = (t) => `<p style="margin:0 0 ${FRAMED_LAYOUT.gap}px;font-size:${FRAMED_LAYOUT.size}px;`
    + `line-height:${FRAMED_LAYOUT.line};color:${EMAIL_INK.body};white-space:pre-wrap">`
    + `${esc(t).replace(/\n/g, "<br />")}</p>`;
  // A cell that holds only an image still reserves a text line otherwise, and that reserved line is
  // exactly the "gap of raw background" her eye caught between the banner and the frame.
  // (It carried a `ground` argument for the frame caps, which round 6 deleted. The argument went
  // with them rather than being left for somebody to find and use — R165 one size down.)
  const cell = (inner) => `<td style="font-size:0;line-height:0;padding:0">${inner}</td>`;
  const img = (src, alt = "") => `<img src="${esc(src)}" width="${W}" alt="${esc(alt)}" `
    + `style="width:100%;max-width:${W}px;height:auto;border:0;display:block" />`;

  // **BULLETPROOF MEANS A REAL `<a>` WITH REAL PADDING, NEVER A BACKGROUND IMAGE** — an image button
  // is a grey box in every client that blocks images by default. The plate is a `<td>` because that
  // is the one way Outlook's Word engine draws a rounded, bordered plate at all; the `<a>` inside it
  // carries the padding, so the whole face is tappable even where the table styling is dropped.
  //
  // **ROUND 4, HER GAP 5: IT HOLDS ONE LINE ON A PHONE.** *UNLOCK MY 12TH HOUSE* at 16px with 2px of
  // tracking and 30px of padding measures ~290px, against ~301px of usable width inside a 393px
  // phone at the desktop padding — so it wrapped to two lines on the one control the whole mail is
  // pointing at. **`white-space:nowrap` is the half that cannot be stripped**, and it is INLINE
  // rather than in the media query for exactly that reason: a client that drops the `<style>` block
  // still gets one line. The media query then shrinks the tracking and the padding so nowrap has
  // room to be true rather than overflowing (R19: the inline half must be safe on its own).
  const button = cta && cta.href
    ? `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:6px 0 0">`
      + `<tr><td style="background:${EMAIL_PLUM};border:1px solid ${EMAIL_INK.gold};border-radius:4px;padding:0">`
      + `<a class="hx-cta" href="${esc(cta.href)}" style="display:inline-block;padding:15px 30px;color:${EMAIL_INK.gold};`
      + `font-size:16px;letter-spacing:2px;text-transform:uppercase;text-decoration:none;font-weight:bold;white-space:nowrap">`
      + `${esc(cta.label || "Open")}</a></td></tr></table>`
    : "";

  // The close: her divider with its centred mark, then her quiet lines beside the lantern — all
  // INSIDE the frame, because that is where her master puts them.
  // **THE ONE DIVIDER (lifted 2026-08-14).** It was written out here and her block composer needed
  // the same mark; the second copy is what would have drifted (R28/R29).
  const closeMark = houseEmailDivider({ top: 30, bottom: 0 });
  // >>> **THE CLOSE CARRIES NO ORNAMENT, BY HER WORD (round 4, gap 2).** <<< The lantern-and-crystals
  // image sat in a second cell beside these lines and **collided with them on a phone**, where the
  // two cells of a two-column table stack. Her ruling: *"those closing lines stand clean without
  // ornament."* The slot is DELETED at its declaration rather than passed null (DL-2/DT-2b/R181) —
  // a retired slot sitting in a signature is one a later round wires back by finding it — and the
  // slice is gone from `build-email-assets.mjs` too, so there is no file to rediscover either.
  const closeBlock = (footerText || signoff)
    ? closeMark
      + `<p style="margin:14px 0 0;font-size:15px;line-height:1.7;color:${EMAIL_INK.soft};white-space:pre-wrap">`
      + `${esc(footerText)}</p>`
      + `<p style="margin:14px 0 0;font-size:15px;line-height:1.7;color:${EMAIL_INK.gold}">`
      + `${esc(signoff)}<br />${esc(siteLine)}</p>`
    : "";

  // >>> **ROUND 5 (owner, 2026-08-13) — THE BODY CARRIES NO ORNAMENT AT ALL, AND THAT IS A RULING
  // RATHER THAN A RETREAT.** <<< Her words after two failed rounds of seams: *"remove the botanical
  // corner images ENTIRELY. The body is the clean rich plum panel with the thin gold frame, nothing
  // stamped on it, no image blocks, no seams possible. The mockup's spirit wins over its every
  // literal leaf — a clean panel that matches beats ornament that shows its edges."*
  //
  // **SO THE SLOT IS DELETED, NOT PASSED NULL** (DL-2/DT-2b/R181). Round 3 laid the branches as
  // opaque rectangles and they seamed; round 4 cut them with a real alpha channel and they STILL
  // read as blocks to her eye. A slot left in this signature is one a later round fills by finding
  // it, and the whole point of her ruling is that **no seam is possible** — which is only true when
  // there is nothing to lay on the panel.
  //
  // >>> **ROUND 6 — THE FRAME IS A BORDER, NOT A PICTURE, AND THAT IS WHAT KILLS HER BLACK BAND.**
  // <<< Her note: *"the black band between banner and body becomes the plum with the gold frame
  // outlining it cleanly — no black strip, no ragged edge."*
  //
  // **MEASURED DOWN THE CENTRE COLUMN OF THE ROUND-5 CAPTURE RATHER THAN GUESSED AT (R25/R180):**
  // her banner's own artwork ends in near-black at y=679, and **y=680..731 read `#21111c` against
  // the panel's `#1e0c17`** — a fifty-pixel band that is not the panel and not the banner. The cause
  // is the alpha cut: the top cap is a strip of her MOCKUP, and every semi-opaque pixel in it hazes
  // its ground over the plum. There is no alpha threshold that fixes that, because the band IS the
  // art's own field being partly kept.
  //
  // **SO THE CAPS ARE DELETED AT THEIR DECLARATION** (DL-2/DT-2b/R181) — the `frame` slot, both
  // rows, and the two slices in `public/`, so nothing wires them back by finding a file (R165) — and
  // the outline becomes what it renders as anyway: **one gold rule on all four sides of the panel.**
  // Same reasoning she applied to the botanicals in round 5, one element up: *a clean panel that
  // matches beats ornament that shows its edges.*
  //
  // **AND THE TOP BORDER IS THE JOIN.** With no cap row between them the banner butts straight onto
  // the panel and the gold line sits exactly on that seam, so there is no strip of anything between
  // her picture and her words — which is her sentence read literally.
  const bg = `background-color:${EMAIL_INK.panel};`
    + `border:1px solid ${EMAIL_INK.gold};`;

  const after = (Array.isArray(afterRule) ? afterRule : [afterRule]).filter(Boolean);

  // >>> **ROUND 6 (owner, 2026-08-13) — THE PHONE GETS A CLOSER CUT OF HER BANNER, AND ROUND 5's
  // NO-CROP RULING IS MIGRATED AT ITS DECLARATION RATHER THAN LEFT STANDING BESIDE IT**
  // (DL-2/DT-2b/R181, this repo's most-repeated instrument defect, refused here in prose too). <<<
  //
  // Round 5's words were *"if a crop exists anywhere in the pipeline, remove it"*, and they were
  // aimed at a crop that **decapitated her logo and left a candle reading like punctuation**. Round
  // 6's words are the other half of the same complaint: *"the mobile banner treatment SCALES THE
  // CENTER so 'Your room in the 12th House is waiting.' and the 12THHOUSE.AI plate read clearly."*
  // **The ruling was never against cropping, it was against THAT crop** — so what returns is a cut
  // measured against her own picture rather than chosen: full height, so nothing is decapitated, and
  // horizontal edges past both candle flames, so neither lands at the margin (see
  // `scripts/build-email-assets.mjs`, where the flame columns are measured).
  //
  // **THE DESKTOP IMAGE IS THE ONE THAT SHIPS INLINE AND THE PHONE ONE IS THE IMPROVEMENT** (R19): a
  // client that strips the `<style>` block shows her banner WHOLE at full width, which is round 5's
  // state exactly — the failure mode of the removable half is the previous shipped behaviour and
  // never something worse. `mso-hide:all` keeps Outlook's Word engine, which honours no media query,
  // from drawing both.
  const wide = banner && banner.src
    ? `<a href="${esc(home)}" class="hx-wide">${img(banner.src, banner.alt || SITE_NAME)}</a>`
    : "";
  const narrow = banner && banner.narrow
    ? `<div class="hx-narrow" style="display:none;max-height:0;overflow:hidden;mso-hide:all">`
      + `<a href="${esc(home)}">${img(banner.narrow, banner.alt || SITE_NAME)}</a></div>`
    : "";
  const bannerCell = wide + narrow;

  return `<!doctype html><html><head><meta charset="utf-8" />`
    // **`width=device-width` IS WHY A DESKTOP MAIL CAN RENDER AT PHONE WIDTH AT ALL**, and its absence
    // is why some clients zoomed this out instead of laying it out: with no viewport declared, a
    // mobile client picks its own and a desktop client is left to guess at a table with no floor.
    + `<meta name="viewport" content="width=device-width,initial-scale=1" />`
    + framedEmailStyle({ width: W, pad: FRAMED_LAYOUT.pad, padPhone: FRAMED_LAYOUT.padPhone })
    + `</head><body style="margin:0;padding:0;background:${EMAIL_INK.ground}">`
    + (preheader
      ? `<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent">${esc(preheader)}</div>` : "")
    + `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${EMAIL_INK.ground};width:100%"><tr><td align="center" style="padding:0">`
    // >>> **ROUND 6 — THE COLUMN WAS PINNED AT ITS FULL WIDTH ON EVERY PHONE SINCE ROUND 4, AND THAT
    // IS THE REAL CAUSE OF HER MOBILE NOTE. MEASURED, NOT REASONED (R25/R180).** <<<
    //
    // Round 4 widened this column and wrote `width:680px;max-width:100%`, which READS correct and is
    // not: for a TABLE the specified width drives the min-content contribution, so `max-width` never
    // gets to clamp it. Measured in a real Chrome at a 393px viewport, `document.scrollWidth` came
    // back **680** — and bisected against five candidates, only this one moved it (images: 680,
    // nowrap: 680, the width ATTRIBUTE: 680, `width:100%;max-width:680px`: **393**).
    //
    // **SO EVERY PHONE HAS EITHER SCROLLED HER MAIL SIDEWAYS OR ZOOMED IT TO 58%, AND AT 58% HER
    // TAGLINE AND HER PLATE ARE UNREADABLE — WHICH IS HER ROUND-6 NOTE (b), ARRIVING FROM A CAUSE
    // NOBODY HAD MEASURED.** The banner crop above genuinely helps and is not what was wrong; **it
    // would have been a repair aimed one layer under the defect**, and this is said plainly rather
    // than folded into the round's own story (R19: a false finding about healthy art costs the same
    // as one about healthy code).
    //
    // **THE `width` ATTRIBUTE STAYS AT THE PIXEL COUNT** because Outlook's Word engine reads only
    // that and honours no percentage sanely; the CSS is what every other client reads. And a
    // `min-width` was written here in round 4 and removed before it shipped, for the same reason one
    // layer up: `min-width` OUTRANKS `max-width`, so it would have re-pinned exactly this.
    + `<table role="presentation" width="${W}" cellpadding="0" cellspacing="0" border="0" style="width:100%;max-width:${W}px;font-family:Georgia,'Times New Roman',serif">`
    + (bannerCell ? `<tr>${cell(bannerCell)}</tr>` : "")
    // **NO CAP ROW.** The panel's own gold border is the frame now, so her banner butts straight onto
    // it and the rule sits exactly on the join — see the `bg` note above for what that replaced.
    + `<tr><td class="hx-pad" style="${bg}padding:22px ${FRAMED_LAYOUT.pad}px 26px">`
    + (heading ? `<p style="margin:0 0 ${FRAMED_LAYOUT.gap}px;font-size:23px;line-height:1.4;color:${EMAIL_INK.gold}">${esc(heading)}</p>` : "")
    + (Array.isArray(blocks) ? blocks : [blocks]).filter(Boolean).map(para).join("")
    + (rule ? `<p style="margin:26px 0 12px;text-align:center;letter-spacing:3px;font-size:16px;color:${EMAIL_INK.gold}">${esc(rule)}</p>` : "")
    + after.map(para).join("")
    + button
    + closeBlock
    + `</td></tr>`
    + `</table></td></tr></table></body></html>`;
}
